import { computed, Injectable, Signal } from '@angular/core';
import { injectLocalStorage } from 'ngxtension/inject-local-storage';
import { PaymentReference } from '@seatix/shared-domain-checkout';

export interface StoredPaymentReferenceData {
  paymentReference: PaymentReference;
  orderToken: string;
  timestamp: number;
  expiresAt: number;
}

/**
 * Reactive payment reference storage service using ngxtension's injectLocalStorage.
 *
 * This service provides persistent storage for DigitalFemsa payment references,
 * ensuring users can access their payment instructions even after page refreshes
 * or accidental navigation away from the checkout process.
 *
 * Key Features:
 * - Reactive signals for payment reference data and expiration status
 * - Automatic cross-tab synchronization via localStorage events
 * - Built-in expiration handling based on payment reference expiry
 * - Type-safe storage operations with automatic cleanup
 *
 * Usage Example:
 * ```typescript
 * @Component({
 *   template: `
 *     @if (referenceStorage.hasValidReference(); as ref) {
 *       <stx-oxxo-pay-reference [paymentReference]="ref" />
 *     } @else {
 *       <p>No active payment reference</p>
 *     }
 *   `
 * })
 * export class ReferenceComponent {
 *   protected readonly referenceStorage = inject(PaymentReferenceStorageService);
 * }
 * ```
 */
@Injectable({ providedIn: 'root' })
export class PaymentReferenceStorageService {
  readonly #STORAGE_KEY = 'stx_payment_reference';

  /**
   * Reactive signal for stored payment reference data with automatic localStorage synchronization.
   * Uses ngxtension's injectLocalStorage for seamless state management.
   */
  readonly #storedReference =
    injectLocalStorage<StoredPaymentReferenceData | null>(this.#STORAGE_KEY, {
      defaultValue: null,
    });

  /**
   * Computed signal that extracts the payment reference from stored data.
   * Returns null if no reference is stored, data is malformed, or reference has expired.
   */
  public readonly paymentReference: Signal<PaymentReference | null> = computed(
    () => {
      const storedData = this.#storedReference();
      if (!storedData) {
        return null;
      }

      // Check if reference has expired based on payment method expiration
      if (this.#isReferenceExpired(storedData)) {
        // Auto-cleanup expired references
        this.clearReference();
        return null;
      }

      return storedData.paymentReference;
    },
  );

  /**
   * Computed signal that extracts the order token from stored reference data.
   * Returns null if no valid reference exists.
   */
  public readonly orderToken: Signal<string | null> = computed(() => {
    const storedData = this.#storedReference();
    return storedData?.orderToken ?? null;
  });

  /**
   * Computed signal that determines if the stored reference has expired.
   * Returns true if no reference exists or if it's past the payment method expiration time.
   */
  public readonly isExpired: Signal<boolean> = computed(() => {
    const storedData = this.#storedReference();
    if (!storedData) {
      return true;
    }
    return this.#isReferenceExpired(storedData);
  });

  /**
   * Computed signal that checks if a valid, non-expired payment reference exists.
   * Useful for conditional rendering and navigation guards.
   */
  public readonly hasValidReference: Signal<boolean> = computed(
    () => this.paymentReference() !== null && !this.isExpired(),
  );

  /**
   * Save a payment reference to localStorage with associated order token.
   * Automatically triggers reactivity across all consumers.
   *
   * @param paymentReference - The payment reference object to store
   * @param orderToken - The associated order token for navigation context
   */
  saveReference(paymentReference: PaymentReference, orderToken: string): void {
    const data: StoredPaymentReferenceData = {
      paymentReference,
      orderToken,
      timestamp: Date.now(),
      expiresAt: paymentReference.charge.expires_at,
    };
    this.#storedReference.set(data);
  }

  /**
   * Clear the stored payment reference data.
   * Automatically triggers reactivity across all consumers.
   */
  clearReference(): void {
    this.#storedReference.set(null);
  }

  /**
   * Get payment reference by order token if it exists and is valid.
   *
   * @param orderToken - The order token to match against stored reference
   * @returns PaymentReference if found and valid, null otherwise
   */
  getReferenceByOrderToken(orderToken: string): PaymentReference | null {
    const storedData = this.#storedReference();
    if (!storedData || storedData.orderToken !== orderToken) {
      return null;
    }

    if (this.#isReferenceExpired(storedData)) {
      this.clearReference();
      return null;
    }

    return storedData.paymentReference;
  }

  /**
   * Check if the stored reference data has expired based on payment method expiration.
   *
   * @param storedData - The stored reference data to check
   * @returns true if reference is expired
   */
  #isReferenceExpired(storedData: StoredPaymentReferenceData): boolean {
    const currentTime = Date.now() / 1000; // Convert to seconds
    return currentTime > storedData.expiresAt;
  }

  /**
   * Cleanup expired references manually.
   * This method is automatically called by the computed signals but can be invoked manually.
   */
  cleanupExpiredReferences(): void {
    const storedData = this.#storedReference();
    if (storedData && this.#isReferenceExpired(storedData)) {
      this.clearReference();
    }
  }
}
